import React from 'react';
import styled from 'styled-components';

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: ${props => {
    switch (props.size) {
      case 'sm': return '0.5rem 1rem';
      case 'lg': return '1rem 2rem';
      default: return '0.75rem 1.5rem';
    }
  }};
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return props.theme.typography.small;
      case 'lg': return props.theme.typography.body;
      default: return props.theme.typography.body;
    }
  }};
  font-weight: 500;
  line-height: 1;
  border-radius: 50px;
  transition: all ${props => props.theme.transitions.fast};
  cursor: pointer;
  border: none;
  text-decoration: none;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'secondary':
        return `
          background-color: transparent;
          color: ${props.theme.colors.text.primary};
          border: 1px solid ${props.theme.colors.border.medium};

          &:hover:not(:disabled) {
            background-color: ${props.theme.colors.background.secondary};
            border-color: ${props.theme.colors.border.dark};
          }
        `;
      case 'outline':
        return `
          background: ${props.theme.colors.glass?.background || 'rgba(255, 255, 255, 0.1)'};
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid ${props.theme.colors.glass?.border || 'rgba(255, 255, 255, 0.2)'};
          color: ${props.theme.colors.text.primary};
          box-shadow: ${props.theme.shadows?.glass || '0 8px 32px rgba(0, 0, 0, 0.1)'};

          &:hover:not(:disabled) {
            background: ${props.theme.colors.glass?.backgroundHover || 'rgba(255, 255, 255, 0.15)'};
            border: 1px solid ${props.theme.colors.glass?.borderHover || 'rgba(255, 255, 255, 0.3)'};
            transform: translateY(-1px);
            box-shadow: ${props.theme.shadows?.glassHover || '0 12px 40px rgba(0, 0, 0, 0.15)'};
          }
        `;
      case 'ghost':
        return `
          background-color: transparent;
          color: ${props.theme.colors.text.primary};
          border: 1px solid transparent;

          &:hover:not(:disabled) {
            background-color: ${props.theme.colors.background.secondary};
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.accent});
          color: ${props.theme.colors.text.inverse};
          border: none;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
            filter: brightness(1.1);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  onClick,
  type = 'button',
  as,
  to,
  href,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      disabled={disabled}
      onClick={onClick}
      type={type}
      as={as}
      to={to}
      href={href}
      {...props}
    >
      {children}
    </StyledButton>
  );
};